import { BlocklyConfig, ToolboxCategory, ToolboxBlock } from "../types/config";

/**
 * Génère le XML de la toolbox Blockly en fonction de la config.
 * - Si toolboxGrouped = true => on crée une <category> par catégorie.
 * - Sinon => on met tous les blocs en racine.
 */
export function generateToolboxXML(config: BlocklyConfig): string {
    const grouped = config.toolboxGrouped ?? true;

    if (grouped) {
        return categoriesToXML(config.toolboxCategories, config.adminMode);
    } else {
        // On “éclate” tous les blocs en racine
        const allBlocks = config.toolboxCategories.flatMap((cat) => cat.blocks);
        return rootBlocksXML(allBlocks, config.adminMode);
    }
}

/**
 * Construit le XML des catégories :
 *   <xml>
 *     <category name="..." colour="...">
 *       <block type="..." deletable="true" movable="true"></block>
 *       ...
 *     </category>
 *     ...
 *   </xml>
 */
function categoriesToXML(categories: ToolboxCategory[], modeAdmin: boolean): string {
    let xml = '<xml xmlns="https://developers.google.com/blockly/xml">';
    for (const cat of categories) {
        if (cat.blocks.length === 0) continue;
        // La couleur de la catégorie
        const colorAttr = cat.color ? ` colour="${cat.color}"` : '';
        const icon = getCategoryIcon(cat.name);
        const displayName = `${icon} ${cat.name}`;
        xml += `<category name="${displayName}"${colorAttr}>`;

        for (const block of cat.blocks) {
            xml += blockXML(block, modeAdmin);
        }
        xml += '</category>';
    }
    xml += '</xml>';
    return xml;
}

/**
 * Place directement les blocs en racine (pas de <category>).
 */
function rootBlocksXML(blocks: ToolboxBlock[], modeAdmin: boolean): string {
    let xml = '<xml xmlns="https://developers.google.com/blockly/xml">';
    for (const block of blocks) {
        xml += blockXML(block, modeAdmin);
    }
    xml += '</xml>';
    return xml;
}

/**
 * Construit le tag <block ...> pour un bloc donné,
 * en incluant `movable`, `deletable`, `editable`, `updatable`.
 * S'il manque l'une de ces propriétés (false), on force une couleur ;
 * sinon, on ne spécifie pas `colour=...` pour laisser la catégorie
 * gérer la couleur par défaut.
 */
function blockXML({
    type,
    movable,
    deletable,
    editable,
    updatable,
}: ToolboxBlock, modeAdmin: boolean): string {
    return `<block
    type="${type}"
    movable="${modeAdmin || movable}"
    deletable="${modeAdmin || deletable}"
    editable="${modeAdmin || editable}"
    updatable="${modeAdmin || updatable}" 
  ></block>`;
}

function getCategoryIcon(categoryName: string): string {
    const normalized = categoryName.toLowerCase();
    if (normalized.includes('event')) return '🎬';
    if (normalized.includes('mouv') || normalized.includes('move')) return '🏃';
    if (normalized.includes('logic') || normalized.includes('condition')) return '🧠';
    if (normalized.includes('math')) return '➗';
    if (normalized.includes('text') || normalized.includes('phrase')) return '💬';
    if (normalized.includes('variable')) return '🔢';
    if (normalized.includes('function') || normalized.includes('fonct')) return '🛠️';
    if (normalized.includes('action')) return '✨';
    return '🧩';
}
